<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  ContractGenerationQuestionType,
  type ContractGenerationTemplateDraftability,
  type ContractGenerationTemplateQuestionInDb,
  type ContractGenerationTemplateQuestionsResponse,
} from '@/api';
import { TemplateQuestionsService } from '@/services/contractGeneration/templateQuestionsService';
import {
  DRAFTABILITY_FIX,
  QUESTION_TYPE_LABEL,
  boundsPayload,
  describeBounds,
  knownValues,
} from '@/utils/contractGeneration/questions';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import QuestionEditorModal, { type QuestionEditorSubmit } from './QuestionEditorModal.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';

/**
 * The questions a template asks — the authoring half.
 *
 * A template with several documents needs something to tell them apart by, and
 * that something is these: "Language: German / English", "Salary: a band".
 * Whoever drafts answers them, and the answers narrow the documents to the
 * ones consistent with them.
 *
 * The section sits above the documents it governs, because the order reads:
 * here is what this template asks, and here is what answers it.
 */
interface Props {
  templateUid: string;
  questions: ContractGenerationTemplateQuestionInDb[];
  draftability: ContractGenerationTemplateDraftability | null;
  /** Read-only for someone without write access to the template. */
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), { disabled: false });

const emit = defineEmits<{
  /**
   * Every write answers with the whole state — questions, every document's
   * answers, and the verdict — because a write is exactly when those change.
   * The page adopts it rather than re-reading.
   */
  (e: 'updated', value: ContractGenerationTemplateQuestionsResponse): void;
}>();

const message = useGaiaToast();

const editorOpen = ref<boolean>(false);
const editing = ref<ContractGenerationTemplateQuestionInDb | null>(null);
const saving = ref<boolean>(false);
const confirmDeleteUid = ref<string | null>(null);
const busyUid = ref<string | null>(null);

const existingLabels = computed<string[]>(() => props.questions.map(question => question.label));

/** What the owner has to fix, in their words — never the backend's prose. */
const blockingReasons = computed<string[]>(() =>
  (props.draftability?.reasons ?? []).map(reason => DRAFTABILITY_FIX[reason])
);

function openAdd(): void {
  editing.value = null;
  editorOpen.value = true;
}

function openEdit(question: ContractGenerationTemplateQuestionInDb): void {
  editing.value = question;
  editorOpen.value = true;
}

/** One write either way: a question is a label, a hint, a type and a flag. */
async function submitEditor(payload: QuestionEditorSubmit): Promise<void> {
  saving.value = true;
  try {
    const target = editing.value;
    const state: ContractGenerationTemplateQuestionsResponse = target
      ? await TemplateQuestionsService.update(props.templateUid, target.uid, {
          label: payload.label,
          hint: payload.hint,
          required_for_variants: payload.required_for_variants,
        })
      : await TemplateQuestionsService.create(props.templateUid, {
          label: payload.label,
          hint: payload.hint,
          question_type: payload.question_type,
          required_for_variants: payload.required_for_variants,
          ...boundsPayload(payload.question_type, payload.bounds),
        });
    emit('updated', state);
    editorOpen.value = false;
    message.success(target ? 'Question updated.' : 'Question added.');
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save the question.'));
  } finally {
    saving.value = false;
  }
}

async function remove(question: ContractGenerationTemplateQuestionInDb): Promise<void> {
  confirmDeleteUid.value = null;
  busyUid.value = question.uid;
  try {
    const state = await TemplateQuestionsService.remove(props.templateUid, question.uid);
    emit('updated', state);
    message.success(`“${question.label}” removed.`);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to remove the question.'));
  } finally {
    busyUid.value = null;
  }
}

const MENU_OPTIONS = [
  { key: 'edit', label: 'Edit question' },
  { key: 'delete', label: 'Delete question' },
];

function handleMenu(key: string, question: ContractGenerationTemplateQuestionInDb): void {
  if (key === 'edit') openEdit(question);
  if (key === 'delete') confirmDeleteUid.value = question.uid;
}

const gBorder = gaiaNeutral.borderCard;
const gSoft = gaiaNeutral.surfaceSoft;
</script>

<template>
  <n-flex vertical :size="12" data-tour="tpl.questions">
    <n-flex align="flex-start" :size="12">
      <n-flex vertical :size="3">
        <n-flex align="baseline" :size="8">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Questions</gaia-text>
          <gaia-count-pill>{{ props.questions.length }}</gaia-count-pill>
        </n-flex>
        <gaia-text size="dense" tone="tertiary">
          What this template asks before it produces a document. The answers narrow which document
          is used.
        </gaia-text>
      </n-flex>
      <gaia-button
        v-if="!props.disabled"
        size="small"
        :style="{ marginLeft: 'auto' }"
        @click="openAdd"
      >
        <template #icon>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </template>
        Add question
      </gaia-button>
    </n-flex>

    <!-- Why the template can't be drafted from, as things to fix. Shown here
         rather than at the top of the page: both reasons are fixed in this
         section or in the documents right below it. -->
    <gaia-banner v-if="blockingReasons.length > 0" type="warning" title="Not ready to draft from">
      <n-flex vertical :size="4">
        <n-flex
          v-for="(reason, index) in blockingReasons"
          :key="index"
          align="center"
          :size="6"
          :wrap="false"
        >
          <gaia-status-indicator status="warning" />
          <gaia-text size="dense" tone="text">{{ reason }}</gaia-text>
        </n-flex>
      </n-flex>
    </gaia-banner>

    <gaia-empty-state v-if="props.questions.length === 0" dashed title="No questions">
      A template with one document needs none. Add a question as soon as there is more than one — it
      is what tells them apart.
      <template v-if="!props.disabled" #actions>
        <gaia-button size="small" @click="openAdd">Add the first question</gaia-button>
      </template>
    </gaia-empty-state>

    <div v-else class="question-list">
      <div v-for="question in props.questions" :key="question.uid" class="question-row">
        <n-flex align="flex-start" :size="10" :wrap="false" class="question-body">
          <n-flex vertical :size="4" :style="{ minWidth: 0, flex: 1 }">
            <n-flex align="center" :size="6" :wrap="true">
              <gaia-text size="dense" weight="semibold" tone="ink">{{ question.label }}</gaia-text>
              <gaia-tag size="tiny" round>
                {{ QUESTION_TYPE_LABEL[question.question_type] }}
              </gaia-tag>
              <gaia-tag v-if="question.required_for_variants" size="tiny" type="warning" round>
                Every document must answer
              </gaia-tag>
              <gaia-tag v-if="describeBounds(question)" size="tiny" round>
                {{ describeBounds(question) }}
              </gaia-tag>
            </n-flex>
            <gaia-text v-if="question.hint" size="meta" tone="subtle">
              {{ question.hint }}
            </gaia-text>
            <!-- A choice question's answers are whatever the documents have
                 said, so an empty list means none has answered yet — not that
                 the question is broken. -->
            <n-flex
              v-if="question.question_type === ContractGenerationQuestionType.SELECTION"
              :size="6"
              :wrap="true"
            >
              <gaia-tag v-for="value in knownValues(question)" :key="value" size="tiny" round>
                {{ value }}
              </gaia-tag>
              <gaia-text v-if="knownValues(question).length === 0" size="meta" tone="subtle">
                no answers yet — they appear as the documents give them
              </gaia-text>
            </n-flex>
          </n-flex>

          <gaia-dropdown
            v-if="!props.disabled"
            :options="MENU_OPTIONS"
            @select="(key: string) => handleMenu(key, question)"
          >
            <gaia-icon-button :dimension="28" quaternary title="Question actions">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="8" cy="3" r="1.3" />
                <circle cx="8" cy="8" r="1.3" />
                <circle cx="8" cy="13" r="1.3" />
              </svg>
            </gaia-icon-button>
          </gaia-dropdown>
        </n-flex>

        <div v-if="confirmDeleteUid === question.uid" class="question-overlay">
          <gaia-table-row-state
            variant="confirm"
            :title="`Delete “${question.label}”?`"
            confirm-label="Delete question"
            @cancel="confirmDeleteUid = null"
            @confirm="remove(question)"
          >
            Every document keeps its other answers. Nothing is narrowed by this question any more.
          </gaia-table-row-state>
        </div>
      </div>
    </div>

    <question-editor-modal
      v-model:show="editorOpen"
      :question="editing"
      :existing-labels="existingLabels"
      :saving="saving"
      @submit="submitEditor"
    />
  </n-flex>
</template>

<style scoped>
.question-list {
  display: flex;
  flex-direction: column;
  border: 1px solid v-bind(gBorder);
  border-radius: 10px;
  overflow: hidden;
}

.question-row {
  position: relative;
}

.question-body {
  padding: 10px 12px;
}

.question-row + .question-row {
  border-top: 1px solid v-bind(gBorder);
}

/* The confirm strip covers its own row, so the list never reflows mid-decision. */
.question-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: v-bind(gSoft);
}
</style>
